import React, { useEffect, useMemo, useState } from 'react';
import { Link, useHistory, useParams } from 'react-router-dom';
import styled from 'styled-components';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { Container } from '../layout/Container';
import Button from '../components/Button';
import VideoPlayer from '../components/VideoPlayer';
import ENV_CONFIG from '../config/environment-unified';
import useTranslation from '../hooks/useTranslation';

function findMatchInBracket(bracket, matchId) {
  const target = String(matchId);
  const seen = new Set();

  const walk = (node) => {
    if (!node || typeof node !== 'object') return null;
    if (seen.has(node)) return null;
    seen.add(node);

    // Common shapes: seed/match with { id }, or nested { seeds: [...] }
    if (node.id && String(node.id) === target) return node;

    for (const key of Object.keys(node)) {
      const val = node[key];
      if (Array.isArray(val)) {
        for (const item of val) {
          const found = walk(item);
          if (found) return found;
        }
      } else if (val && typeof val === 'object') {
        const found = walk(val);
        if (found) return found;
      }
    }
    return null;
  };

  return walk(bracket);
}

const MatchDetails = () => {
  const { slug, matchId } = useParams();
  const history = useHistory();
  const { t } = useTranslation();
  const user = useSelector(state => state.user.data);

  const [loading, setLoading] = useState(true);
  const [event, setEvent] = useState(null);
  const [match, setMatch] = useState(null);

  const [streams, setStreams] = useState([]);
  const [selectedPov, setSelectedPov] = useState(null);
  const [hasAccess, setHasAccess] = useState(true);
  const [checkingAccess, setCheckingAccess] = useState(false);
  const [replays, setReplays] = useState([]);
  const [loadingReplays, setLoadingReplays] = useState(false);
  const [eventReplays, setEventReplays] = useState([]);
  const [loadingEventReplays, setLoadingEventReplays] = useState(false);
  const [selectedReplayId, setSelectedReplayId] = useState('');
  const [attachingReplay, setAttachingReplay] = useState(false);

  const matchStatus = useMemo(() => {
    if (!match) return null;
    if (match.playing) return 'live';
    if (match.finished) return 'finished';
    return 'pending';
  }, [match]);

  useEffect(() => {
    let mounted = true;
    async function load() {
      try {
        setLoading(true);

        const eventRes = await axios.get(`${ENV_CONFIG.EVENTS_ENDPOINT}/events/${slug}`, {
          withCredentials: true
        });
        const ev = eventRes.data?.event;
        if (!mounted) return;
        setEvent(ev);

        // Access check for paid events (match page must not leak POVs)
        const priceNum = (() => {
          const raw = ev?.ticketPrice;
          const n = typeof raw === 'string' ? parseFloat(raw) : Number(raw);
          return Number.isFinite(n) ? n : 0;
        })();

        const isOrg = !!ev?.createdBy && !!user?.id && String(ev.createdBy) === String(user.id);
        if (priceNum <= 0 || isOrg) {
          setHasAccess(true);
        } else if (!user?.id) {
          setHasAccess(false);
        } else {
          setCheckingAccess(true);
          try {
            // Tournament players/streamers are tracked via eventStreamer, not participants tickets.
            // If the user is an active assignee for this tournament event, grant access.
            if (ev?.category?.slug === 'tournament') {
              try {
                const playersRes = await axios.get(`${ENV_CONFIG.EVENTS_ENDPOINT}/events/${slug}/players`, {
                  withCredentials: true
                });
                const list = playersRes.data?.players || [];
                const isAssignee = list.some((p) => String(p?.user?.id) === String(user.id));
                if (isAssignee) {
                  setHasAccess(true);
                  return;
                }
              } catch (_) {
                // ignore and continue with ticket check
              }
            }

            const participantsRes = await axios.get(`${ENV_CONFIG.EVENT_PARTICIPANTS_ENDPOINT}/${ev.id}`, {
              withCredentials: true
            });
            const participants = participantsRes.data?.participants || [];
            const me = participants.find((p) => String(p.userId) === String(user.id));
            setHasAccess(!!me?.ticketId);
          } catch (_) {
            setHasAccess(false);
          } finally {
            setCheckingAccess(false);
          }
        }

        if (!ev?.tournamentId) {
          toast.error(t('matchdetails.no_tournament_linked', 'This event has no tournament linked'));
          history.push(`/events/${slug}`);
          return;
        }

        // Fetch bracket via existing endpoint used by current UI (stable through gateway mapping)
        const tourRes = await axios.get(`${ENV_CONFIG.TOURNAMENT_ENDPOINT}/getTournament/${ev.tournamentId}`, {
          withCredentials: true
        });
        const bracket = tourRes.data?.bracket;
        const found = findMatchInBracket(bracket, matchId);
        if (!mounted) return;
        setMatch(found);

        // Load live POVs for this event (so match page can link POVs immediately)
        try {
          const povRes = await axios.get(`/api/stream/streams/by-event/${ev.id}`, { withCredentials: true });
          const list = povRes.data?.streams || [];
          if (!mounted) return;
          setStreams(list);
          setSelectedPov(list[0] || null);
        } catch {
          if (!mounted) return;
          setStreams([]);
          setSelectedPov(null);
        }

        // Load match replays (public)
        try {
          setLoadingReplays(true);
          const repRes = await axios.get(`/api/stream/replays/by-match/${matchId}`, { withCredentials: true });
          if (!mounted) return;
          setReplays(repRes.data?.replays || []);
        } catch {
          if (!mounted) return;
          setReplays([]);
        } finally {
          if (mounted) setLoadingReplays(false);
        }
      } catch (e) {
        console.error('❌ Failed to load match details:', e);
        toast.error(t('matchdetails.failed_to_load_match', 'Failed to load match'));
        history.push(`/events/${slug}`);
      } finally {
        if (mounted) setLoading(false);
      }
    }

    load();
    return () => { mounted = false; };
  }, [slug, matchId, history, user?.id, t]);

  const isOrganizer = !!event?.createdBy && !!user?.id && String(event.createdBy) === String(user.id);

  useEffect(() => {
    let mounted = true;
    async function loadEventReplays() {
      if (!isOrganizer || !event?.id) return;
      try {
        setLoadingEventReplays(true);
        const res = await axios.get(`/api/stream/replays/by-event/${event.id}`, { withCredentials: true });
        if (!mounted) return;
        setEventReplays(res.data?.replays || []);
      } catch {
        if (!mounted) return;
        setEventReplays([]);
      } finally {
        if (mounted) setLoadingEventReplays(false);
      }
    }
    loadEventReplays();
    return () => { mounted = false; };
  }, [isOrganizer, event?.id]);

  const handleAttachReplay = async () => {
    if (!isOrganizer || !selectedReplayId || !event?.id) return;
    try {
      setAttachingReplay(true);
      await axios.post(
        `/api/stream/replays/${selectedReplayId}/attach`,
        { eventId: event.id, matchId },
        { withCredentials: true }
      );
      toast.success(t('matchdetails.replay_attached', 'Replay attached to match'));
      setSelectedReplayId('');
      // refresh
      const repRes = await axios.get(`/api/stream/replays/by-match/${matchId}`, { withCredentials: true });
      setReplays(repRes.data?.replays || []);
    } catch (e) {
      console.error('Error attaching replay:', e);
      toast.error(e.response?.data?.error || t('matchdetails.failed_attach_replay', 'Failed to attach replay'));
    } finally {
      setAttachingReplay(false);
    }
  };

  if (loading) {
    return (
      <Container style={{ paddingTop: '4rem', paddingBottom: '2rem' }}>
        <h2>{t('common.loading', 'Loading...')}</h2>
      </Container>
    );
  }

  if (!event) {
    return (
      <Container style={{ paddingTop: '4rem', paddingBottom: '2rem' }}>
        <h2>{t('eventdetails.event_not_found', 'Event not found')}</h2>
      </Container>
    );
  }

  if (!match) {
    return (
      <Container style={{ paddingTop: '4rem', paddingBottom: '2rem' }}>
        <HeaderRow>
          <div>
            <h2 style={{ margin: 0 }}>{t('matchdetails.match', 'Match')}</h2>
            <div style={{ marginTop: 6, color: '#9CA3AF' }}>{t('matchdetails.match_not_found_in_bracket', 'Match not found in bracket')}</div>
          </div>
          <Link to={`/events/${event.slug}`}>
            <Button variant="secondary" size="small">{t('matchdetails.back_to_event', 'Back to event')}</Button>
          </Link>
        </HeaderRow>
      </Container>
    );
  }

  const teams = match.teams || [];
  const teamA = teams[0];
  const teamB = teams[1];

  return (
    <Container style={{ paddingTop: '4rem', paddingBottom: '2rem' }}>
      <HeaderRow>
        <div>
          <Title>{t('matchdetails.match', 'Match')}</Title>
          <Meta>
            <Link to={`/events/${event.slug}`}>{event.name}</Link>
            <Dot>•</Dot>
            <Status status={matchStatus}>{matchStatus}</Status>
          </Meta>
        </div>
        <Actions>
          <Link to={`/events/${event.slug}/watch`}>
            <Button variant="primary" size="small">{t('matchdetails.watch_event', 'Watch Event')}</Button>
          </Link>
          <Link to={`/events/${event.slug}`}>
            <Button variant="secondary" size="small">{t('common.back', 'Back')}</Button>
          </Link>
        </Actions>
      </HeaderRow>

      <Grid>
        <Card>
          <SectionTitle>{t('matchdetails.teams', 'Teams')}</SectionTitle>
          <TeamsRow>
            <TeamBlock>
              <TeamName>{teamA?.player?.name || t('eventdashboard.tbd', 'TBD')}</TeamName>
              <TeamScore>{teamA?.score ?? '-'}</TeamScore>
            </TeamBlock>
            <Vs>{t('eventdashboard.vs', 'vs')}</Vs>
            <TeamBlock>
              <TeamName>{teamB?.player?.name || t('eventdashboard.tbd', 'TBD')}</TeamName>
              <TeamScore>{teamB?.score ?? '-'}</TeamScore>
            </TeamBlock>
          </TeamsRow>
        </Card>

        <Card>
          <SectionTitle>{t('eventWatch.livePov', 'Live POV')}</SectionTitle>
          {!hasAccess ? (
            <AccessBlock>
              <h3 style={{ marginTop: 0 }}>{t('eventWatch.premiumContent', 'Premium content')}</h3>
              <p style={{ color: '#9CA3AF', marginTop: 8 }}>
                {t('matchdetails.need_access_match_pov', 'You need access to watch POV streams for this match.')}
              </p>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 14 }}>
                <Link to={`/events/${event.slug}/watch`}>
                  <Button variant="primary" size="small">
                    {checkingAccess
                      ? t('matchdetails.checking', 'Checking...')
                      : `${t('eventWatch.getAccess', 'Get Access')} – ${event.ticketPrice} ${t('common.gcoins', 'G-Coins')}`}
                  </Button>
                </Link>
                <Link to={`/events/${event.slug}`}>
                  <Button variant="secondary" size="small">{t('eventWatch.details', 'Details')}</Button>
                </Link>
              </div>
            </AccessBlock>
          ) : (
            <>
              <PovList>
                {streams.length === 0 ? (
                  <div style={{ color: '#9CA3AF' }}>{t('eventWatch.noLivePovYet', 'No live POV streams yet.')}</div>
                ) : (
                  streams.map((s) => (
                    <PovButton
                      key={s.id}
                      active={selectedPov?.id === s.id}
                      onClick={() => setSelectedPov(s)}
                    >
                      <AvatarImg src={s.avatar || 'https://via.placeholder.com/24'} alt={s.username} />
                      <span>{s.username}</span>
                    </PovButton>
                  ))
                )}
              </PovList>

              {selectedPov?.playbackLink ? (
                <PlayerWrap>
                  <VideoPlayer
                    key={selectedPov.playbackLink}
                    type="hls"
                    src={selectedPov.playbackLink}
                    isLive={true}
                    isMute={false}
                    autoPlay={true}
                    id="match-details-player"
                  />
                </PlayerWrap>
              ) : (
                <div style={{ color: '#9CA3AF', marginTop: 10 }}>{t('matchdetails.no_playback_available', 'No playback available.')}</div>
              )}
            </>
          )}
        </Card>

        <Card>
          <SectionTitle>{t('matchdetails.replay', 'Replay')}</SectionTitle>
          {loadingReplays ? (
            <div style={{ color: '#9CA3AF' }}>{t('matchdetails.loading_replays', 'Loading replays...')}</div>
          ) : replays.length === 0 ? (
            <div style={{ color: '#9CA3AF' }}>{t('matchdetails.no_replay_linked', 'No replay linked to this match yet.')}</div>
          ) : (
            <ReplayList>
              {replays.map((r) => (
                <ReplayRow key={r.id}>
                  <div>
                    <div style={{ color: '#E5E7EB', fontWeight: 800 }}>{r.title}</div>
                    <div style={{ color: '#9CA3AF', fontSize: 13 }}>{r.description || ''}</div>
                  </div>
                  <Link to={`/replay/${r.id}`}>
                    <Button variant="secondary" size="small">{t('matchdetails.watch_replay', 'Watch replay')}</Button>
                  </Link>
                </ReplayRow>
              ))}
            </ReplayList>
          )}

          {isOrganizer && (
            <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid #2D3748' }}>
              <div style={{ color: '#E5E7EB', fontWeight: 800, marginBottom: 8 }}>{t('matchdetails.attach_replay_title', 'Attach replay to this match')}</div>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                <select
                  value={selectedReplayId}
                  onChange={(e) => setSelectedReplayId(e.target.value)}
                  style={{ padding: '10px 12px', borderRadius: 10, border: '1px solid #2D3748', background: '#0f172a', color: '#E5E7EB', minWidth: 260 }}
                  disabled={loadingEventReplays}
                >
                  <option value="">
                    {loadingEventReplays ? t('common.loading', 'Loading...') : t('matchdetails.select_event_replay', 'Select an event replay...')}
                  </option>
                  {eventReplays.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.title} {r.user?.username ? `— ${r.user.username}` : `— user ${r.userId}`}
                    </option>
                  ))}
                </select>
                <Button
                  variant="primary"
                  size="small"
                  disabled={!selectedReplayId || attachingReplay}
                  onClick={handleAttachReplay}
                >
                  {attachingReplay ? t('matchdetails.attaching', 'Attaching...') : t('matchdetails.attach', 'Attach')}
                </Button>
              </div>
              <div style={{ marginTop: 8, color: '#9CA3AF', fontSize: 12 }}>
                v1: replays auto-link to the event; organizer attaches them to the correct match (supports multiple streamers).
              </div>
            </div>
          )}
        </Card>
      </Grid>
    </Container>
  );
};

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 16px;
`;

const Title = styled.h2`
  margin: 0;
  color: ${({ theme }) => theme.color.darkerText};
`;

const Meta = styled.div`
  margin-top: 6px;
  color: ${({ theme }) => theme.color.lighterText};
  display: flex;
  align-items: center;
  gap: 8px;
  a { color: ${({ theme }) => theme.color.primary}; }
`;

const Dot = styled.span`
  opacity: 0.6;
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
`;

const Status = styled.span`
  font-weight: 800;
  padding: 4px 10px;
  border-radius: 999px;
  color: white;
  background: ${({ status }) =>
    status === 'live' ? '#F59E0B' :
    status === 'finished' ? '#6B7280' :
    '#10B981'
  };
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
`;

const Card = styled.div`
  border-radius: 12px;
  border: 1px solid #2D3748;
  background: #0b1220;
  padding: 16px;
`;

const SectionTitle = styled.div`
  color: #E5E7EB;
  font-weight: 800;
  margin-bottom: 10px;
`;

const TeamsRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  gap: 12px;
  align-items: center;
`;

const TeamBlock = styled.div`
  border: 1px solid #2D3748;
  border-radius: 12px;
  padding: 14px;
  background: rgba(255,255,255,0.03);
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const TeamName = styled.div`
  color: #E5E7EB;
  font-weight: 800;
`;

const TeamScore = styled.div`
  color: #E5E7EB;
  font-weight: 900;
  font-size: 18px;
`;

const Vs = styled.div`
  color: #9CA3AF;
  font-weight: 900;
`;

const PovList = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const PovButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border-radius: 10px;
  border: 1px solid ${({ active, theme }) =>
    active ? theme.color.primary : (theme.mode === 'dark' ? '#2D3748' : theme.color.border)};
  background: ${({ active, theme }) =>
    active
      ? (theme.mode === 'dark' ? 'rgba(102,126,234,0.15)' : 'rgba(69,105,255,0.12)')
      : (theme.mode === 'dark' ? '#0b1220' : theme.color.secondaryBg)};
  color: ${({ theme }) => (theme.mode === 'dark' ? '#E5E7EB' : theme.color.darkerText)};
  cursor: pointer;
`;

const AccessBlock = styled.div`
  border: 1px solid #2D3748;
  background: rgba(0,0,0,0.35);
  border-radius: 12px;
  padding: 16px;
`;

const AvatarImg = styled.img`
  width: 24px;
  height: 24px;
  border-radius: 999px;
  object-fit: cover;
`;

const PlayerWrap = styled.div`
  margin-top: 12px;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #2D3748;
  background: #000;
`;

const ReplayList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const ReplayRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border-radius: 12px;
  border: 1px solid #2D3748;
  background: rgba(255,255,255,0.03);
`;

export default MatchDetails;



